import { createI18n } from 'vue-i18n';
import moment from 'moment';
import {
    DEFAULT_LOCALE,
    LOCALE_CACHE_KEY,
    buildPublicLocaleCache,
    buildLocaleCache,
    getPublicLocale,
    getMomentLocale,
    normalizeLocale,
} from './locales';
import { setRuntimeI18n } from './runtimeState';

const loadLocaleMessages = () => {
    // Keep translation files scalable: flat files like
    // src/i18n/translations/<locale>/<namespace>.json become
    // messages[locale][namespace], and nested feature files like
    // src/i18n/translations/<locale>/<feature>/<area>.json become
    // messages[locale][feature][area].
    const messages = {};
    const context = require.context(
        './translations',
        true,
        /^\.\/[a-z-]+(?:\/[a-zA-Z0-9-_]+)+\.json$/
    );

    context.keys().forEach((key) => {
        const match = key.match(/^\.\/([^/]+)\/(.+)\.json$/);
        if (!match) return;

        const [, locale, namespacePath] = match;
        const namespaceParts = namespacePath.split('/');
        messages[locale] = messages[locale] || {};

        let target = messages[locale];
        namespaceParts.slice(0, -1).forEach((part) => {
            target[part] = target[part] || {};
            target = target[part];
        });

        target[namespaceParts[namespaceParts.length - 1]] = context(key);
    });

    return messages;
};

const messages = loadLocaleMessages();

export const i18n = createI18n({
    legacy: false,
    globalInjection: true,
    locale: getPublicLocale(),
    fallbackLocale: DEFAULT_LOCALE,
    messages,
});

setRuntimeI18n(i18n);

export const setLocale = (locale) => {
    // This is the only place that should mutate Vue I18n's active locale and
    // the document language; alpha policy is resolved before calling it.
    const normalizedLocale = normalizeLocale(locale);

    i18n.global.locale.value = normalizedLocale;

    if (typeof document !== 'undefined') {
        document.documentElement.lang = normalizedLocale;
    }
    moment.locale(getMomentLocale(normalizedLocale));
};

export const setPublicLocale = (locale) => {
    const normalizedLocale = normalizeLocale(locale);

    localStorage.setItem(
        LOCALE_CACHE_KEY,
        JSON.stringify(buildPublicLocaleCache(normalizedLocale))
    );
    setLocale(normalizedLocale);
};

export const writeLocaleCache = (user, effectiveLocale) => {
    // Cache only the server-confirmed effective locale after /user/me or save.
    // Do not use this cache to activate French before alpha access is known.
    if (!user?.id) return;

    localStorage.setItem(
        LOCALE_CACHE_KEY,
        JSON.stringify(buildLocaleCache(user, effectiveLocale))
    );
};

export const clearLocaleCache = () => {
    // Logout removes user-specific locale state, while preserving the browser's
    // public-page language choice for login, registration, and reset screens.
    const publicLocale = getPublicLocale();
    localStorage.setItem(
        LOCALE_CACHE_KEY,
        JSON.stringify(buildPublicLocaleCache(publicLocale))
    );
    setLocale(publicLocale || DEFAULT_LOCALE);
};

setLocale(i18n.global.locale.value);

export default i18n;
